<script setup lang="ts">
import { ref, onMounted, computed, defineProps } from "vue";
import { displayService } from '@/_services';

const props = defineProps<{
  headerColor: string;
  textColor: string;
  header?: Array<{ text: string }>;
}>();

const rates = ref<any[]>([]);

const codeToFlag = {
  USD: 'us',
  EUR: 'eu',
  GBP: 'gb',
  JPY: 'jpy',
  CAD: 'ca',
  CHF: 'ch',
  AED: 'aed',
  SAR: 'sa',
  QAR: 'qa',
  KWD: 'kw',
  BHD: 'bh',
  LYD: 'lyd',
  CNY: 'cny',
};

onMounted(async () => {
  try {
    const response = await displayService.getExchangeData();
    const changes = response.changes || [];
    const updatedRates = await Promise.all(
      changes.map(async (item: any) => {
        let flagSrc = '';
        try {
          const code = String(item.code);
          const flagFile = (codeToFlag as Record<string, string>)[code] || code.toLowerCase();
          const flagModule = await import(`@/assets/images/flags/${flagFile}.svg`);
          flagSrc = flagModule.default;
        } catch (error) {
          flagSrc = '';
        }
        return { ...item, flagSrc };
      })
    );
    rates.value = updatedRates;
  } catch (err) {
    console.error("Error loading exchange data:", err);
  }
});

const headers = computed(() => {
  if (
    props.header &&
    Array.isArray(props.header) &&
    props.header.length > 0 &&
    !(props.header.length === 1 && props.header[0].text === "")
  ) {
    return props.header.map((h) => h.text);
  }
  return ["DEVISE", "CODE", "UNITÉ", "ACHAT", "VENTE"];
});
</script>

<script lang="ts">
export default {
  name: "ExchangeTable",
};
</script>

<template>
  <div class="exchange-table-widget">
    <table class="exchange-table">
      <thead>
        <tr :style="{ color: headerColor }">
          <th
            v-for="(header, index) in headers"
            :key="index"
            :class="{ 'first-header': index === 0, 'other-header': index > 0 }"
          >
            {{ header }}
          </th>
        </tr>
      </thead>
      <tbody :style="{ color: textColor }">
        <tr
          v-for="(rate, index) in rates"
          :key="index"
          :class="{ 'even-row': index % 2 === 1 }"
        >
          <td class="devise-col">
            <div class="d-flex align-items-center gap-2">
              <img
                v-if="rate.flagSrc"
                :src="rate.flagSrc"
                class="flag-img"
                :alt="rate.devise"
              />
              
            </div>
          </td>
          <td class="code-col">{{ rate.code }}</td>
          <td class="achat-col">{{ rate.buy.toFixed(3) }}</td>
          <td class="vente-col">{{ rate.sell.toFixed(3) }}</td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<style scoped>
.exchange-table-widget {
  width: 100%;
  height: 100%;
  overflow: auto;
  font-family: Arial, sans-serif;
  display: flex !important;
  flex-direction: column;
}

.exchange-table {
  width: 100%;
  height: 100%;
  border-collapse: collapse;
  text-align: left;
  display: table !important;
}

.exchange-table thead tr {
  border-bottom: 1px solid currentColor;
}

.exchange-table th {
  padding: 8px 12px;
  font-weight: bold;
  white-space: nowrap;
  text-transform: uppercase;
}

.first-header {
  text-align: start;
}

.other-header {
  text-align: center;
}

.exchange-table td {
  padding: 8px 12px;
}

.devise-col {
  text-transform: uppercase;
  font-size: 0.9em;
}

.unite-col,
.achat-col,
.vente-col {
  text-align: center;
}

.code-col {
  text-align: center;
}

.flag-container {
  display: flex !important;
  align-items: center;
  gap: 8px;
}

.flag-img {
  width: auto !important;
  height:24px !important;
  /* object-fit: contain; */
}

.even-row {
  background-color: #f5f5f5;
}
</style>
